import {
  ParteBucalEnum,
  SituacaoCoroaEnum,
  SituacaoFaceEnum,
  SituacaoRaizEnum,
  TipoParteBucalEnum,
} from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'

import { FillColor } from '../odontograma/types/Coroa'
import { ArcadaFieldModel } from './arcadas-field/ArcadasField'
import { DenteFieldModel } from './dentes-field/DentesField'
import {
  condicoesDente,
  condicoesQueDesabilitamCoroa,
  dentesComLesaoDeFurca,
  dentesDeciduos,
  OdontogramaPosicaoProtese,
  partesBucal,
} from './model'
import { SextanteFieldModel } from './sextantes-field/SextantesField'

export function getParteBucal(parteBucalEnum: ParteBucalEnum) {
  return partesBucal[parteBucalEnum]
}

export function getCondicaoDente(condicao: SituacaoRaizEnum | SituacaoFaceEnum) {
  return condicoesDente[condicao]
}

export function getParteBucalFieldPath(key: ParteBucalEnum) {
  const path = metaPath<SoapState>()
  const parteBucal = getParteBucal(key)

  switch (parteBucal.type) {
    case TipoParteBucalEnum.ARCADA:
      return path.plano.odontograma.arcadas[key]

    case TipoParteBucalEnum.SEXTANTE:
      return path.plano.odontograma.sextantes[key]

    case TipoParteBucalEnum.DENTE:
      return path.plano.odontograma.dentes[key]
  }
}

export function getParteBucalByDenteNumber(n: number) {
  return getDentes().find((dente) => getParteBucal(dente).number === n)
}

export function getFillColorBySituacaoCoroa(
  situacaoCoroa: SituacaoCoroaEnum,
  fillColors: FillColor<SituacaoCoroaEnum>[]
) {
  return fillColors.find((fillColor) => fillColor.value === situacaoCoroa) || undefined
}

export function getArcadas() {
  return (Object.keys(partesBucal) as Array<ParteBucalEnum>)
    .filter((item) => getParteBucal(item).type === TipoParteBucalEnum.ARCADA)
    .sort((a, b) => getParteBucal(a).title.localeCompare(getParteBucal(b).title))
}

export function getSextantes() {
  return (Object.keys(partesBucal) as Array<ParteBucalEnum>)
    .filter((item) => getParteBucal(item).type === TipoParteBucalEnum.SEXTANTE)
    .sort((a, b) => getParteBucal(a).title.localeCompare(getParteBucal(b).title))
}

export function getDentes() {
  return (Object.keys(partesBucal) as Array<ParteBucalEnum>)
    .filter((item) => getParteBucal(item).type === TipoParteBucalEnum.DENTE)
    .sort((a, b) => getParteBucal(a).title.localeCompare(getParteBucal(b).title))
}

export function isDenteDeciduo(dente: ParteBucalEnum) {
  return dentesDeciduos.includes(dente)
}

export function isDentePermanente(dente: ParteBucalEnum) {
  return !isDenteDeciduo(dente)
}

export function isCoroaDisabled(dente: DenteFieldModel) {
  return dente?.condicoesDente?.some((item) => condicoesQueDesabilitamCoroa.includes(item))
}

export function possuiLesaoDeFurca(dente: ParteBucalEnum) {
  return dentesComLesaoDeFurca.includes(dente)
}

export function getDentePosicaoProtese(dente: ParteBucalEnum): OdontogramaPosicaoProtese {
  const numeroDente = getParteBucal(dente).number
  const arcadaSuperiorDenteRange = (numeroDente >= 11 && numeroDente <= 28) || (numeroDente >= 51 && numeroDente <= 65)

  return arcadaSuperiorDenteRange ? 'superior' : 'inferior'
}

export function getPartesBucalComProcedimento(
  items:
    | Record<ParteBucalEnum, DenteFieldModel>
    | Record<ParteBucalEnum, SextanteFieldModel>
    | Record<ParteBucalEnum, ArcadaFieldModel>
): ParteBucalEnum[] {
  return (
    items &&
    Object.entries(items).map(
      ([key, item]: [ParteBucalEnum, SextanteFieldModel | ArcadaFieldModel | DenteFieldModel]) =>
        item.procedimentos?.length > 0 && key
    )
  )
}

export function getDentesComDados(
  items: Record<ParteBucalEnum, DenteFieldModel>,
  proteseTotalSuperior?: boolean,
  proteseTotalInferior?: boolean
): ParteBucalEnum[] {
  return (
    items &&
    Object.entries(items)
      .map(
        ([key, item]: [ParteBucalEnum, DenteFieldModel]) =>
          (item.procedimentos?.length > 0 ||
            item.condicoesDente?.length > 0 ||
            (getDentePosicaoProtese(key) === 'superior' ? proteseTotalSuperior : proteseTotalInferior)) &&
          key
      )
      .filter((i) => !!i)
  )
}
